import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { components, palette } from '../../utils/designTokens';
import { useSignalInvestigation } from '../../services/classifiersApi';
import { getTableCellColor, getTableRowStyle, tableGridHeaderClassName, tablePanelTitleClassName } from '../TablePrimitives';

const formatNumber = (value) => Number(value || 0).toLocaleString();
const expandedRowBg = 'var(--color-table-row-expanded)';
const tableHeaderBg = 'var(--color-table-header-bg)';
const groupGridTemplate = '24px minmax(0, 1.25fr) 80px minmax(140px, 0.95fr) minmax(140px, 0.85fr) minmax(180px, 0.95fr)';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const topValuesText = (items = [], fallback = '-') => {
  const values = (Array.isArray(items) ? items : [])
    .slice(0, 2)
    .map(item => item?.value || item?.workflow_name || '')
    .filter(Boolean);
  return values.length > 0 ? values.join(', ') : fallback;
};

const shortId = (value, length = 8) => {
  if (!value) return '';
  return String(value).slice(0, length);
};

const isNonEmpty = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'number') return value > 0;
  return String(value).trim() !== '';
};

const DebugLine = ({ label, value }) => (
  isNonEmpty(value) ? (
    <span className="truncate" title={`${label}: ${value}`}>
      <span style={{ color: palette.text.muted }}>{label}: </span>
      <span style={{ color: palette.text.primary }}>{value}</span>
    </span>
  ) : null
);

const SectionTitle = ({ children, right }) => (
  <div className="flex items-center justify-between gap-3">
    <h3 className={tablePanelTitleClassName} style={{ color: palette.text.supporting }}>
      {children}
    </h3>
    {right}
  </div>
);

const SmallMetric = ({ label, value }) => (
  <div className="min-w-0">
    <div className="text-[11px]" style={{ color: palette.text.muted }}>{label}</div>
    <div className="text-sm font-semibold tabular-nums" style={{ color: palette.text.primary }}>
      {formatNumber(value)}
    </div>
  </div>
);

const TimeBars = ({ buckets = [] }) => {
  const values = (Array.isArray(buckets) ? buckets : []).map(bucket => Number(bucket?.detected_count || 0));
  const max = Math.max(...values, 0);
  if (!values.length || max <= 0) return <span style={{ color: palette.text.muted }}>-</span>;
  return (
    <div className="flex items-end gap-1 h-8 min-w-[180px]" aria-label="Time histogram">
      {values.slice(-32).map((value, index) => (
        <span
          key={`${index}-${value}`}
          className="block flex-1 rounded-sm"
          style={{
            height: `${Math.max(3, Math.round((value / max) * 28))}px`,
            minWidth: '3px',
            maxWidth: '8px',
            backgroundColor: palette.accents.primary.base,
            opacity: 0.35 + ((value / max) * 0.55),
          }}
          title={`${formatNumber(value)} detected`}
        />
      ))}
    </div>
  );
};

const ValueList = ({ title, items = [] }) => {
  const visible = (Array.isArray(items) ? items : []).slice(0, 5);
  if (visible.length === 0) return null;
  return (
    <div className="min-w-0">
      <div className="text-[11px] mb-1 font-semibold uppercase tracking-wide" style={{ color: palette.text.supporting }}>{title}</div>
      <div className="flex flex-col gap-1">
        {visible.map(item => (
          <div key={`${title}-${item.value || item.workflow_name}`} className="flex items-center justify-between gap-2 text-xs">
            <span className="truncate" style={{ color: palette.text.primary }} title={item.value || item.workflow_name}>
              {item.value || item.workflow_name}
            </span>
            <span className="tabular-nums" style={{ color: getTableCellColor('metric') }}>
              {formatNumber(item.count)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const SampleTraceRow = ({ sample, index = 0, isLast = false, onOpenTrace }) => {
  const debug = sample?.debug_fields || {};
  const spanStatus = String(debug.status_code || '').toUpperCase();
  const primaryIssue = [
    debug.http_status_code ? `HTTP ${debug.http_status_code}` : '',
    debug.exception_type,
    spanStatus === 'ERROR' ? 'Span ERROR' : '',
  ].filter(Boolean).slice(0, 2).join(' / ');
  const traceContext = [
    sample?.workflow_name || '',
    sample?.user_id ? `User ${shortId(sample.user_id, 10)}` : '',
    formatDateTime(sample?.start_time),
  ].filter(Boolean).join(' · ');
  const traceIds = [
    sample?.trace_id ? `Trace ${shortId(sample.trace_id)}` : '',
    sample?.session_id ? `Session ${shortId(sample.session_id, 10)}` : '',
  ].filter(Boolean).join(' · ');
  const reason = sample?.what_happened || sample?.reason || '';
  const primaryContext = [traceContext || 'Trace', reason].filter(Boolean).join(' · ');
  const modelContext = [sample?.model, sample?.provider].filter(Boolean).join(' / ');
  const spanLabel = sample?.span_name
    ? `Span: ${sample.span_name}${sample?.span_kind ? ` (${sample.span_kind})` : ''}`
    : '';
  const issueContext = [
    sample?.agent ? `Agent: ${sample.agent}` : '',
    sample?.tool ? `Tool: ${sample.tool}` : '',
    spanLabel,
    spanStatus === 'ERROR' ? `Status: ${debug.status_code}` : '',
    modelContext ? `Model: ${modelContext}` : '',
  ].filter(Boolean).join(' · ');
  const rowStyle = getTableRowStyle(index, {
    interactive: true,
    style: {
      borderBottom: isLast ? 'none' : `1px solid ${components.card.borderColor}`,
    },
  });

  return (
    <button
      type="button"
      className="group w-full text-left p-3 transition-colors hover:opacity-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px]"
      style={{
        ...rowStyle,
        outlineColor: palette.accents.primary.base,
      }}
      aria-label={`Open trace ${sample?.trace_id ? shortId(sample.trace_id) : ''}`}
      onClick={() => onOpenTrace?.(sample)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-xs font-medium" style={{ color: getTableCellColor('entity') }} title={primaryContext}>
            {primaryContext}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <div className="text-[11px] tabular-nums" style={{ color: getTableCellColor('muted') }}>
            {traceIds}
          </div>
          <span
            className="inline-flex items-center gap-1 rounded border px-2 py-1 text-[11px] font-medium transition-opacity group-hover:opacity-100"
            style={{
              color: palette.accents.primary.base,
              borderColor: palette.neutrals.borderStrong,
              backgroundColor: palette.neutrals.surfaceSubtle,
            }}
          >
            Open trace <ExternalLink size={12} />
          </span>
        </div>
      </div>
      <div className="mt-2 grid gap-1 text-[11px]" style={{ color: getTableCellColor('muted') }}>
        <DebugLine label="Message" value={debug.error_message || debug.status_message} />
        <DebugLine label="Evidence" value={sample?.evidence_summary} />
        <DebugLine label="Detail" value={primaryIssue} />
        <DebugLine label="Context" value={issueContext} />
      </div>
    </button>
  );
};

const GroupDetails = ({ group, onViewTraces, onOpenTrace }) => (
  <div
    className="border-t p-3"
    style={{
      borderColor: components.card.borderColor,
      backgroundColor: expandedRowBg,
    }}
  >
    <div
      className="rounded-md border overflow-hidden"
      style={{ borderColor: components.card.borderColor, backgroundColor: palette.neutrals.panelPrimary }}
    >
      <div className="px-3 py-2 border-b" style={{ borderColor: components.card.borderColor, backgroundColor: tableHeaderBg }}>
        <SectionTitle>Analysis</SectionTitle>
      </div>
      <div className="grid gap-4 p-3" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
        <ValueList title="Agents" items={group?.where?.agents} />
        <ValueList title="Tools" items={group?.where?.tools} />
        <ValueList title="Models / Providers" items={[...(group?.where?.models || []), ...(group?.where?.providers || [])]} />
        <ValueList title="Span Shape" items={group?.where?.span_shapes} />
        <ValueList title="Business Tasks" items={group?.impact?.workflows} />
        <ValueList title="Users" items={group?.impact?.users} />
        <ValueList title="HTTP / Exceptions" items={[...(group?.debug_fields?.http_status_codes || []), ...(group?.debug_fields?.exception_types || [])]} />
      </div>
    </div>

    <div
      className="mt-3 rounded-md border overflow-hidden"
      style={{ borderColor: components.card.borderColor, backgroundColor: palette.neutrals.panelPrimary }}
    >
      <div className="flex items-center justify-between gap-3 px-3 py-2 border-b" style={{ borderColor: components.card.borderColor, backgroundColor: tableHeaderBg }}>
        <SectionTitle>Evidence Examples</SectionTitle>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs font-medium"
          style={{ color: palette.accents.primary.base }}
          onClick={() => onViewTraces?.(group)}
        >
          Show all <ExternalLink size={12} />
        </button>
      </div>
      <div>
        {(group?.sample_traces || []).length === 0 ? (
          <div className="p-3 text-xs" style={{ color: palette.text.muted }}>No example traces returned.</div>
        ) : (
          group.sample_traces.map((sample, index) => (
            <SampleTraceRow
              key={`${sample.classifier_result_id}-${sample.trace_id}`}
              sample={sample}
              index={index}
              isLast={index === group.sample_traces.length - 1}
              onOpenTrace={onOpenTrace}
            />
          ))
        )}
      </div>
    </div>
  </div>
);

const GroupRow = ({ group, expanded, index, onToggle, onViewTraces, onOpenTrace }) => {
  const Icon = expanded ? ChevronDown : ChevronRight;
  const rowRef = useRef(null);
  const rowStyle = getTableRowStyle(index, {
    interactive: true,
    style: {
      gridTemplateColumns: groupGridTemplate,
      scrollMarginTop: '16px',
      ...(expanded ? { backgroundColor: tableHeaderBg } : {}),
    },
  });

  useEffect(() => {
    if (!expanded || !rowRef.current) return undefined;
    const frame = window.requestAnimationFrame(() => {
      rowRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [expanded]);

  return (
    <div>
      <button
        ref={rowRef}
        type="button"
        className={`w-full grid items-center gap-3 px-3 py-2 text-left transition-colors hover:opacity-95 ${components.table.row.border}`}
        style={rowStyle}
        onClick={onToggle}
      >
        <Icon size={16} style={{ color: palette.text.muted }} />
        <div className="min-w-0">
          <div className="text-sm font-medium truncate" style={{ color: getTableCellColor('entity') }} title={group?.display_label}>
            {group?.display_label}
          </div>
          <div className="text-[11px] truncate" style={{ color: getTableCellColor('body') }} title={group?.what_happened}>
            {group?.what_happened || group?.reason || '-'}
          </div>
        </div>
        <div className="text-sm font-semibold tabular-nums text-right" style={{ color: getTableCellColor('metric') }}>
          {formatNumber(group?.count)}
        </div>
        <div className="text-xs truncate pl-4" style={{ color: getTableCellColor('muted') }} title={topValuesText([...(group?.where?.agents || []), ...(group?.where?.tools || [])])}>
          {topValuesText([...(group?.where?.agents || []), ...(group?.where?.tools || [])])}
        </div>
        <div className="text-xs truncate" style={{ color: getTableCellColor('muted') }} title={topValuesText(group?.impact?.workflows)}>
          {topValuesText(group?.impact?.workflows)}
        </div>
        <TimeBars buckets={group?.time_buckets} />
      </button>
      {expanded && (
        <GroupDetails
          group={group}
          onViewTraces={onViewTraces}
          onOpenTrace={onOpenTrace}
        />
      )}
    </div>
  );
};

export const SignalInvestigationContent = ({
  selectedAppId,
  signal,
  enabled = true,
  onViewTraces,
  onOpenTrace,
  className = 'flex flex-col gap-5',
}) => {
  const classifierKey = signal?.classifierType || signal?.key || '';
  const [expandedKey, setExpandedKey] = useState(null);
  const lastAutoExpandedTokenRef = useRef('');
  const { investigation, loading, error } = useSignalInvestigation(selectedAppId, classifierKey, {
    enabled: Boolean(enabled && selectedAppId && classifierKey),
    limitGroups: 10,
    limitBreakdowns: 5,
    limitSamples: 3,
  });

  useEffect(() => {
    setExpandedKey(null);
    lastAutoExpandedTokenRef.current = '';
  }, [classifierKey]);

  const groups = useMemo(() => (
    Array.isArray(investigation?.groups) ? investigation.groups : []
  ), [investigation?.groups]);
  const groupsAutoExpandToken = useMemo(() => (
    groups.length > 0
      ? `${classifierKey}:${groups.map(group => group.group_key).join('|')}`
      : ''
  ), [classifierKey, groups]);

  useEffect(() => {
    if (!groupsAutoExpandToken) return;
    if (lastAutoExpandedTokenRef.current === groupsAutoExpandToken) return;
    lastAutoExpandedTokenRef.current = groupsAutoExpandToken;
    setExpandedKey(groups[0].group_key);
  }, [groups, groupsAutoExpandToken]);

  const summary = investigation?.summary || {};

  return (
    <div className={className}>
      {loading ? (
        <div className="text-sm" style={{ color: palette.text.muted }}>Loading investigation...</div>
      ) : error ? (
        <div className="text-sm" style={{ color: palette.status.danger }}>{error}</div>
      ) : (
        <>
          <div className={`${components.card.bg} ${components.card.rounded} p-3`} style={{ border: `1px solid ${components.card.borderColor}`, backgroundColor: palette.neutrals.panelPrimary }}>
            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
              <SmallMetric label="Detected" value={summary.detected_count} />
              <SmallMetric label="Users" value={summary.affected_users} />
              <SmallMetric label="Sessions" value={summary.affected_sessions} />
              <SmallMetric label="Business Tasks" value={summary.affected_workflows} />
              <div className="min-w-0">
                <div className="text-[11px]" style={{ color: palette.text.muted }}>Latest</div>
                <div className="text-sm font-semibold truncate" style={{ color: palette.text.primary }}>
                  {formatDateTime(summary.latest_seen)}
                </div>
              </div>
            </div>
          </div>

          <div
            className={`${components.card.bg} ${components.card.rounded} p-4`}
            style={{ border: `1px solid ${components.card.borderColor}`, backgroundColor: palette.neutrals.panelPrimary }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className={tablePanelTitleClassName} style={{ color: palette.text.supporting }}>
                Findings
              </h3>
            </div>
            {groups.length === 0 ? (
              <div className="border rounded-md p-3 text-sm" style={{ borderColor: components.card.borderColor, backgroundColor: palette.neutrals.panelPrimary, color: palette.text.muted }}>
                No findings for this range.
              </div>
            ) : (
              <div className="overflow-hidden rounded-md border" style={{ borderColor: components.card.borderColor }}>
                <div
                  className={tableGridHeaderClassName}
                  style={{
                    gridTemplateColumns: groupGridTemplate,
                    columnGap: '12px',
                  }}
                >
                  <span />
                  <span>Pattern</span>
                  <span className="text-right">Detections</span>
                  <span className="pl-4">Location</span>
                  <span>Affected</span>
                  <span>Trend</span>
                </div>
                {groups.map((group, index) => (
                  <GroupRow
                    key={group.group_key}
                    group={group}
                    index={index}
                    expanded={expandedKey === group.group_key}
                    onToggle={() => setExpandedKey(prev => (prev === group.group_key ? null : group.group_key))}
                    onViewTraces={onViewTraces}
                    onOpenTrace={onOpenTrace}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SignalInvestigationContent;
